// /ui — app shell. DOM over canvas (ADR-2).
//
// **P2.12 emptied this file of its original contents.** From P0.6 to P2.11 it was the tone
// generator's surface: a play/stop button, a frequency slider, a gain slider, a master meter and a
// latency readout, all wired to `AudioEngine`. Every one of those was scaffolding for a walking
// skeleton — proof that a click could reach a speaker through a worklet and a SAB ring, built
// before there was anything worth playing. The deck is the instrument now, so the scaffolding came
// down and what is left is the deck, the scene, and the two diagnostics that were never scaffolding.
//
// Three Stack §2 disciplines survive the rewrite, because they were the point rather than the demo:
//
//   * **No React state in any rAF or SAB-reading path.** The deck's meters and readouts are written
//     with direct DOM through refs, from their own loops (see `Transport.tsx`). Putting the audio
//     thread's output on React's schedule would cost a reconcile per frame, forever, to move a bar.
//   * **The transferred canvas is owned by an effect, not by JSX.** An `OffscreenCanvas` is a
//     one-way resource and React is entitled to remount an element whenever it likes.
//   * **The render-path toggle stays.** It is P0.8's, not P0.6's: it is the only way to exercise the
//     main-thread fallback that Tier 2 degradation depends on, and a fallback nobody can reach is a
//     fallback nobody trusts. Whether a *performer* should see it is a UI question for M3's shell,
//     and it is parked as one rather than answered by deletion here.

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { DeckPanel, PerformLayout } from './PerformLayout';
import { TrackLoader, useTrackLoader, WaveformLane, type TrackLoaderApi } from './deck';
import { AudioGraph, DRIFT_SAMPLE_MS, MIN_DRIFT_WINDOWS } from '../audio/engine';
import { ControlBus } from '../control';
import { MixerPanel, useMeters, useMixer } from './mixer';
import { AnalysisCache } from '../library';
import type { RenderPath } from '../platform/types/render';
import { startLatencyReporting } from '../telemetry';
import { readRenderStats } from '../visual/core/render-loop';
import {
  createRendererHost,
  defaultRenderPath,
  supportsOffscreenCanvas,
  type RendererHost,
} from '../visual/core/render-host';

const FPS_POLL_MS = 500;

/**
 * One deck's panel and its contents (P3.7).
 *
 * A local component rather than two copies of the same JSX. It takes the loader alone and reads the
 * deck id off it, so the panel's identity colour and the controls inside it cannot end up describing
 * different decks — see `TrackLoaderApi.deckId`.
 *
 * `DeckColumn`, not `Deck`: `Deck` is the engine class this eventually drives, and two things with
 * one name in one codebase is a grep that returns the wrong file every time.
 */
function DeckColumn({
  loader,
  partner,
}: {
  readonly loader: TrackLoaderApi;
  /** The other deck, for FR-07's sync (P3.9) — the one control that is about the pair. */
  readonly partner: TrackLoaderApi;
}): JSX.Element {
  return (
    <DeckPanel
      deckId={loader.deckId}
      dropActive={loader.dropActive}
      onDragOver={loader.onDragOver}
      onDragLeave={loader.onDragLeave}
      onDrop={loader.onDrop}
    >
      <TrackLoader loader={loader} partner={partner} />
    </DeckPanel>
  );
}

const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 270;

export function App(): JSX.Element {
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<RendererHost | null>(null);
  const fpsRef = useRef<HTMLSpanElement | null>(null);
  const underrunRef = useRef<HTMLSpanElement | null>(null);

  // The one flag (P0.8). Switching it tears the host down and builds the other one; everything
  // below — scene, loop, stats, readout — is identical either way, which is the claim being made.
  const [renderPath, setRenderPath] = useState<RenderPath>(() => defaultRenderPath());

  /**
   * The one `AudioContext`, opened on demand and shared by both decks (P3.7).
   *
   * A promise in a ref rather than state, and opened lazily rather than at mount, because both facts
   * are load-bearing:
   *
   *   * **Lazily**, because a context created outside a user gesture starts suspended and stays
   *     there. The first thing that happens to either deck is a drop or a file-picker click, and
   *     both are gestures.
   *   * **The promise, not the graph**, so two tracks dropped on the two decks within the same tick
   *     get one context between them. Storing the resolved value instead would leave a window where
   *     both decks see `null`, both call `AudioGraph.start()`, and the app quietly runs two contexts
   *     — which is exactly the bug this prompt exists to avoid, arrived at from the other side.
   *
   * Not a module singleton: FR-19's cue device is a second graph at M5, and a global would have to
   * be unpicked to get one.
   */
  const graphRef = useRef<Promise<AudioGraph> | null>(null);
  /**
   * The graph once it is actually open, for consumers that must not open one themselves (P4.10).
   *
   * ⚠️ The mixer is such a consumer. Handing it `openGraph` let it create an `AudioContext` at page
   * load, outside a user gesture — suspended, and inherited by every deck built on it. Handing it a
   * resolved graph or `null` makes that impossible by type rather than by comment.
   */
  const [openedGraph, setOpenedGraph] = useState<AudioGraph | null>(null);
  const openGraph = useCallback((): Promise<AudioGraph> => {
    graphRef.current ??= AudioGraph.start().then((graph) => {
      setOpenedGraph(graph);
      return graph;
    });
    return graphRef.current;
  }, []);

  /**
   * FR-12's analysis cache, opened once and shared by both decks (P3.8).
   *
   * The same promise-in-a-ref shape as the graph above, for the same reason: two decks loading in
   * the same tick must get *one* IndexedDB connection between them. Two connections at the same
   * version are harmless, but two at different versions block each other's upgrade — and the schema
   * version is exactly what P3.8 bumps to invalidate the cache.
   *
   * `AnalysisCache.open` resolves to `null` rather than throwing when the browser will not have one
   * (a private window, storage denied, no OPFS), so this promise never rejects and the decks below
   * never need a failure path. A cache is an optimisation; its correct behaviour when unavailable is
   * to be absent.
   */
  const cacheRef = useRef<Promise<AnalysisCache | null> | null>(null);
  const openCache = useCallback((): Promise<AnalysisCache | null> => {
    cacheRef.current ??= AnalysisCache.open();
    return cacheRef.current;
  }, []);

  // FR-01's load path (P2.5), once per deck (P3.7). Each owns its own deck, its own control bus and
  // its own keyboard source; they share the graph, the cache, and nothing else.
  /**
   * The mixer's control bus (P4.10).
   *
   * ⚠️ **A third bus, and Architecture §3 describes one.** `useTrackLoader` creates a `ControlBus`
   * per deck, so the app already runs two; this makes three. It works today because every
   * subscriber is scoped to the surface that publishes to it, and it will stop working at **FF-1**,
   * where MIDI learn is a single source that must reach every control. Consolidating is a change to
   * how the decks own their bus and belongs in its own prompt, not smuggled into the mixer's.
   * Recorded in `BACKLOG-NOTES.md` under P4.10.
   */
  const mixerBusRef = useRef<ControlBus | null>(null);
  mixerBusRef.current ??= new ControlBus();
  const mixerBus = mixerBusRef.current;

  // FR-14's mixer (P4.10). Takes the same `openGraph` the decks take and never calls it for a
  // gesture of its own — see `useMixer`: a context opened outside a user gesture starts suspended
  // and stays there.
  const mixer = useMixer(mixerBus, openedGraph);
  // P4.7's meters. Refs only — every value goes straight to the DOM from one rAF loop (Stack §2).
  const meters = useMeters(openedGraph);
  const deckA = useTrackLoader('a', { openGraph, openCache });
  const deckB = useTrackLoader('b', { openGraph, openCache });

  /**
   * ⚠️ The loader, kept in a ref because **`useTrackLoader` returns a fresh object every render**
   * and two long-lived effects below were keyed on it (P4.16).
   *
   * An effect with `[deckA]` in its dependency list is torn down and restarted every time this
   * component renders, which restarts its `setInterval` from zero. A timer therefore only ever
   * fires if the app goes that long without rendering — and this app renders on load progress,
   * analysis progress, transport phase, drop state and every mixer move.
   *
   * **Measured, not theorised:** P4.16's watchdog poll is 100 ms and wrote nothing at all during a
   * 3.3-second end-to-end test, because it never survived to its first tick.
   *
   * ⚠️ **The same dependency is on the telemetry reporter, whose window is sixty seconds** — so it
   * needs a full minute of no renders to emit anything, and a single fader move resets the clock.
   * That is a **fourth** distinct reason `latency_probe` has never been seen in the field, after the
   * GitHub variable that was set all along, the e2e rebuild that stripped the key, and the CSP. This
   * one is ours, in our own React.
   *
   * Reading the getters off a captured loader is safe, which is what makes a ref the right fix
   * rather than a hack: `latencyInfo` and `underruns` read `deckRef.current` inside the hook, so a
   * loader object captured minutes ago still answers with today's deck.
   */
  const deckARef = useRef(deckA);
  useEffect(() => {
    deckARef.current = deckA;
  });

  /**
   * The context, closed when the app goes away.
   *
   * Each deck's own hook disposes its node; nobody but this owns the context, so nobody else can
   * close it. Reading the ref rather than `openGraph()` on purpose: calling the opener in a cleanup
   * would *create* a context in order to close it, on an unmount where a track was never loaded.
   */
  useEffect(() => {
    return () => {
      void graphRef.current?.then((graph) => graph.close());
      void cacheRef.current?.then((cache) => cache?.close());
    };
  }, []);

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (container === null) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    canvas.className = 'render-canvas';
    container.append(canvas);

    const host = createRendererHost(
      canvas,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
      globalThis.devicePixelRatio,
      renderPath,
    );
    hostRef.current = host;

    return () => {
      hostRef.current = null;
      host.dispose();
      canvas.remove();
    };
  }, [renderPath]);

  // The FPS readout. Direct DOM from a poll, for the same reason as everything else that reads a
  // SharedArrayBuffer: this is the render thread's own counter, and React has no business on the
  // path between it and a span.
  useEffect(() => {
    let previousCount = 0;
    let previousTime = performance.now();

    const id = setInterval(() => {
      const host = hostRef.current;
      const node = fpsRef.current;
      if (host === null || node === null) {
        return;
      }

      const stats = readRenderStats(host.stats);
      const now = performance.now();
      const fps = ((stats.frameCount - previousCount) * 1000) / (now - previousTime);
      previousCount = stats.frameCount;
      previousTime = now;

      node.textContent =
        `${fps.toFixed(1)} FPS · frame ${stats.lastFrameMs.toFixed(2)} ms ` +
        `(worst ${stats.maxFrameMs.toFixed(2)} ms) · ${host.path}`;
    }, FPS_POLL_MS);

    return () => {
      clearInterval(id);
    };
  }, [renderPath]);

  // ⚠️ Architecture §12's watchdog, which until P4.16 was a counter with nothing feeding it.
  //
  // `sampleClockDrift` existed from P0.12 and **only `/bench` ever called it**, so every real
  // session reported zero underruns — not because the audio thread was healthy, but because nobody
  // had asked it. A counter nobody reads and a counter that reads zero are the same number, which is
  // the failure this repo has now met at P0.1, P0.5, P0.8, P0.10, P0.11, P2.12 and here.
  //
  // **Deck A only, and that is not a simplification.** Both decks share one AudioContext since P3.7
  // — "the context belongs to the graph" — and the monitor compares `context.currentTime` against
  // `performance.now()`. Deck B's monitor would measure the same two clocks and produce the same
  // answer, so polling both would double the cost to learn nothing. Deck A is the one that is
  // always present.
  //
  // Direct DOM from a poll, like the FPS readout above and for the same reason: this is the audio
  // thread's own clock and React has no business on the path between it and a span.
  useEffect(() => {
    // ⚠️ **Sampled at 10 Hz, read at 2 Hz, and the split is not cosmetic.** `sample()` is O(1);
    // `report` walks every window the run has accumulated to sum the late blocks, and a session
    // accumulates one window every two seconds — 1,800 of them in an hour, 14,400 in a long night.
    // Reading it ten times a second would be O(n) work at 10 Hz against an n that never stops
    // growing, on the thread the visuals live on. The readout has no use for 10 Hz anyway: nobody
    // reads a number that changes faster than they can look at it.
    const readEvery = Math.max(1, Math.round(FPS_POLL_MS / DRIFT_SAMPLE_MS));
    let tick = 0;

    const id = setInterval(() => {
      const loader = deckARef.current;
      loader.sampleClockDrift();

      tick += 1;
      if (tick % readEvery !== 0) {
        return;
      }

      const node = underrunRef.current;
      if (node === null) {
        return;
      }

      const report = loader.underruns;
      if (report === null) {
        // No deck yet, so no monitor and nothing to watch — the deck is built on the first load.
        // Said out loud rather than left on a perpetual "listening…", because a watchdog that looks
        // like it is working and is not is the whole reason this poll exists.
        node.dataset['evaluated'] = 'false';
        node.dataset['lateBlocks'] = '0';
        node.dataset['clockOffsetPpm'] = '';
        node.dataset['maxDriftMs'] = '';
        node.dataset['worstWindowExcessMs'] = '';
        node.textContent = 'audio · idle, no track loaded';
        return;
      }

      // The attributes are what the end-to-end suite reads, and they exist so the prose above them
      // can be reworded without breaking a test — a number parsed out of a sentence is a sentence
      // nobody may edit. `driftEvaluated` is the one that must not be collapsed into the count:
      // "not measured yet" and "measured zero" are different facts (see `UnderrunReport`).
      node.dataset['evaluated'] = String(report.evaluated);
      node.dataset['lateBlocks'] = String(report.lateBlocks);
      node.dataset['clockOffsetPpm'] =
        report.clockOffsetPpm === null ? '' : report.clockOffsetPpm.toFixed(2);
      // ⚠️ Published even when `evaluated` is false, and that is the point of having them.
      // `lateBlocks` needs `MIN_DRIFT_WINDOWS` (ten seconds) before it means anything, which is
      // longer than some scenarios *are* — the Sync end-to-end test reaches its assertion in about
      // three. These two say something useful at that timescale: `maxDrift` is the worst excursion
      // seen at all, and `worstWindowExcess` is how close the closest window came to the noise
      // floor, negative being margin. A thread that stalled during a three-second test shows up in
      // both, long before the detector is entitled to a verdict.
      node.dataset['maxDriftMs'] = report.maxDriftMs.toFixed(2);
      node.dataset['worstWindowExcessMs'] =
        report.worstWindowExcessMs === null ? '' : report.worstWindowExcessMs.toFixed(2);

      if (!report.evaluated) {
        node.textContent = `audio · listening… (${String(report.windowCount)}/${String(
          MIN_DRIFT_WINDOWS,
        )} windows)`;
        return;
      }

      const ppm = report.clockOffsetPpm ?? 0;
      node.textContent =
        `audio · ${String(report.lateBlocks)} late block${report.lateBlocks === 1 ? '' : 's'} · ` +
        `clock ${ppm.toFixed(1)} ppm`;
    }, DRIFT_SAMPLE_MS);

    return () => {
      clearInterval(id);
    };
    // No dependencies on purpose — see `deckARef`. A `[deckA]` here restarts the timer on every
    // render, and this one is a 100 ms timer that then never fires.
  }, []);

  // NFR-P1, measured in the field rather than assumed (P2.10, re-pointed at the deck at P2.12).
  //
  // Mounted unconditionally, including before a track exists: the source returns null until there
  // is a deck, which is the same answer it gives for a window nobody touched a control during.
  //
  // **It stopped being quiet at P3.6**, and the note it replaces is worth keeping in mind when
  // reading a dashboard. From P2.12 to P3.6 the deck's ring carried only `SET_RATE` and nothing
  // streamed it, so a normal session produced about one sample per track load — under the reporter's
  // floor, so no event was ever sent. FR-05's fader streams hundreds a minute (measured: 0 samples
  // before touching it, 304 after a 2.2-second sweep), which is what makes this a field measurement
  // rather than a wire that was merely connected.
  useEffect(() => {
    return startLatencyReporting((minimumSamples) => {
      // Deck A's probe. Both decks have one — every deck owns its own command ring and therefore
      // its own Architecture §4.5 histogram — and reporting one of them is the right call rather
      // than a simplification: NFR-P1 asks what a control costs, not what two controls cost
      // together, and summing two distributions produces a p90 that describes neither. Deck A is
      // the one that is always present.
      const window = deckARef.current.takeLatencyWindow(minimumSamples);
      if (window === null) {
        return null;
      }

      const info = deckARef.current.latencyInfo;
      return {
        count: window.count,
        p50Ms: window.p50,
        p90Ms: window.p90,
        p95Ms: window.p95,
        p99Ms: window.p99,
        maxMs: window.max,
        overflow: window.overflow,
        bucketMs: window.bucketMs,
        outputLatencyMs:
          info?.outputLatencySeconds === null || info?.outputLatencySeconds === undefined
            ? null
            : info.outputLatencySeconds * 1000,
        sampleRateHz: info?.sampleRateHz ?? null,
      };
    });
    // ⚠️ No dependencies, and this line is the fix for a four-deep mystery. `[deckA]` restarted the
    // sixty-second window on every render, so it only ever closed if the app sat untouched for a
    // full minute — and a single control move reset it. See `deckARef`.
  }, []);

  return (
    <PerformLayout
      topBar={<span className="micro">SYNAPSE · two decks</span>}
      // Both lanes since P3.13. UX §3.1 stacks them — "Deck B scrolling ribbon (phase-aligned
      // under A)" — and deck A's is drawn *mirrored* so the two ribbons meet in the middle rather
      // than being separated by A's overview strip. Seeing beat land under beat is the entire
      // reason the layout puts them one above the other.
      waveforms={
        <>
          <WaveformLane loader={deckA} />
          <WaveformLane loader={deckB} />
        </>
      }
      stage={
        // ⚠️ A column, not a row, since P4.10. The stage held the render-path
        // diagnostics alone, so a row was fine; the mixer is UX §3.1's centre column and is the
        // product rather than an instrument, so it goes first and the diagnostics sit under it.
        // Squeezed beside the canvas it was clipped at both edges — seen in a browser, not
        // guessed.
        <div className="perform__stage-stack">
          {/* P4.8's FX readout needs to know whether a deck has a grid, and the deck is the side
              that owns that — see `EchoTiming`. Derived here rather than published back from the
              worklet, so there is one answer travelling one way. */}
          <MixerPanel
            mixer={mixer}
            meters={meters}
            hasGrid={{ a: deckA.beatGrid !== null, b: deckB.beatGrid !== null }}
          />
          <div className="row">
            {/* Empty on purpose. The effect above owns the canvas, because a transferred canvas is a
              one-way resource and React is entitled to remount this element whenever it likes. */}
            <div ref={canvasContainerRef} />
            <div className="stack">
              <span ref={fpsRef} className="mono">
                measuring…
              </span>
              {/* Architecture §12's underrun counter, where a person can see it (P4.16). It reads
                  zero on a healthy machine for ever, which is exactly why it has to be visible:
                  an instrument nobody looks at is one nobody notices has stopped. */}
              <span ref={underrunRef} className="mono diagnostics__underruns">
                audio · listening…
              </span>
              <div className="row">
                <label>
                  <input
                    type="radio"
                    name="render-path"
                    checked={renderPath === 'worker'}
                    disabled={!supportsOffscreenCanvas()}
                    onChange={() => {
                      setRenderPath('worker');
                    }}
                  />{' '}
                  Render worker
                </label>
                <label>
                  <input
                    type="radio"
                    name="render-path"
                    checked={renderPath === 'main-thread'}
                    onChange={() => {
                      setRenderPath('main-thread');
                    }}
                  />{' '}
                  Main-thread fallback
                </label>
              </div>
              <small>
                Same scene, same loop, same stats — only the thread differs (Architecture
                §2).
              </small>
            </div>
          </div>
        </div>
      }
      deckA={<DeckColumn loader={deckA} partner={deckB} />}
      deckB={<DeckColumn loader={deckB} partner={deckA} />}
    />
  );
}
